import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { authEnabled, hasStoredSession, loadAuthClient, stashReturnTo } from '@/lib/auth';
import type { AuthSession } from '@/lib/authClient';
import { trackEvent } from '@/hooks/useAnalytics';
import { reportActivity } from '@/lib/activity';

export type AuthStatus = 'disabled' | 'anonymous' | 'authenticated';

export interface SignInOptions {
  /** Which page to open; the gate's "Create free account" wants 'signup'. */
  mode?: 'signin' | 'signup';
  /** Path to return to after auth completes; defaults to the current one. */
  returnTo?: string;
}

export interface AuthContextValue {
  status: AuthStatus;
  email?: string;
  /** Cognito given_name claim; the header greets with this when present. */
  givenName?: string;
  familyName?: string;
  phone?: string;
  marketingOptIn?: boolean;
  /**
   * True while a stored session is being restored on first load. Auth
   * surfaces (header control, read gate) hold back during this brief
   * window so returning readers do not see an anonymous flash.
   */
  restoring: boolean;
  /** Stash the return path, then open the native sign-in or sign-up page. */
  signIn: (opts?: SignInOptions) => void;
  signOut: () => void;
  /**
   * Adopt a session an auth page just established (the authClient has
   * already stored the tokens). Flips status to authenticated, which is
   * what triggers the page's redirect back to the stashed return path.
   * newAccount marks a signup completing (either the auto sign-in or the
   * fallback OTP), so the activity beacon records sign_up, not sign_in.
   */
  adoptSession: (session: AuthSession, method: 'email_otp' | 'google', newAccount?: boolean) => void;
  /**
   * Replace the in-memory session after a profile update (the client has
   * already refreshed the tokens), so the header greeting changes
   * without a re-login. No analytics; this is not a sign-in.
   */
  applySession: (session: AuthSession) => void;
}

const DISABLED_VALUE: AuthContextValue = {
  status: 'disabled',
  restoring: false,
  signIn: () => undefined,
  signOut: () => undefined,
  adoptSession: () => undefined,
  applySession: () => undefined,
};

const AuthContext = createContext<AuthContextValue>(DISABLED_VALUE);

/** Never stash an auth page as the post-auth destination. */
const AUTH_PATHS = ['/signin', '/signup', '/auth/callback'];

export function AuthProvider({ children }: { children: ReactNode }) {
  // When the Cognito env vars are absent at build time the provider is a
  // pass-through: status stays 'disabled' and no auth code ever loads.
  if (!authEnabled) {
    return <AuthContext.Provider value={DISABLED_VALUE}>{children}</AuthContext.Provider>;
  }
  return <EnabledAuthProvider>{children}</EnabledAuthProvider>;
}

function EnabledAuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [session, setSession] = useState<AuthSession | null>(null);
  // Only readers with a stored session go through a restore pass; for
  // everyone else the state is settled (anonymous) from the first render.
  const [restoring, setRestoring] = useState<boolean>(() => hasStoredSession());

  // Restore a previous session on first mount, but only when the cheap
  // localStorage hint says one exists; otherwise anonymous readers never
  // download the auth chunk.
  useEffect(() => {
    if (!hasStoredSession()) return;
    let cancelled = false;
    loadAuthClient()
      .then((client) => client.getSession())
      .then((restored) => {
        if (cancelled) return;
        if (restored) setSession(restored);
        setRestoring(false);
      })
      .catch((err: unknown) => {
        console.error('auth session restore failed', err);
        if (!cancelled) setRestoring(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(
    (opts?: SignInOptions) => {
      // Include the hash: stepped articles keep the reading position there
      // (#step-id), so dropping it would land the reader back on step one
      // after the sign-in round trip.
      const here = window.location.pathname + window.location.search + window.location.hash;
      const fallback = AUTH_PATHS.some((path) => window.location.pathname.startsWith(path))
        ? '/'
        : here;
      stashReturnTo(opts?.returnTo ?? fallback);
      navigate(opts?.mode === 'signup' ? '/signup' : '/signin');
    },
    [navigate],
  );

  const adoptSession = useCallback(
    (adopted: AuthSession, method: 'email_otp' | 'google', newAccount?: boolean) => {
      setSession(adopted);
      setRestoring(false);
      trackEvent('login_success', { method });
      // The beacon needs the stored tokens, so it must fire here (after the
      // client stored them), never earlier in the flow: a pre-token report
      // is silently dropped by getIdToken(). One event per interactive
      // completion: sign_up for a fresh account, sign_in otherwise.
      void reportActivity(newAccount ? 'sign_up' : 'sign_in');
    },
    [],
  );

  const applySession = useCallback((next: AuthSession) => {
    setSession(next);
  }, []);

  const signOut = useCallback(() => {
    trackEvent('logout');
    setSession(null);
    // Local sign-out only: drop the stored tokens, no redirect anywhere.
    loadAuthClient()
      .then((client) => client.signOut())
      .catch((err: unknown) => {
        console.error('sign-out failed', err);
      });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status: session ? 'authenticated' : 'anonymous',
      email: session?.email,
      givenName: session?.givenName,
      familyName: session?.familyName,
      phone: session?.phone,
      marketingOptIn: session?.marketingOptIn,
      restoring,
      signIn,
      signOut,
      adoptSession,
      applySession,
    }),
    [session, restoring, signIn, signOut, adoptSession, applySession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
